// import { useLocalStorage } from "../hooks/useLocalStorage";
import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc'
import { watch } from "fs";
import { useCookies } from "react-cookie";
import jwt_decode, { JwtPayload } from "jwt-decode"
import axios from "../api/axios";

dayjs.extend(utc)

export interface TokenPayload {
    "fresh": boolean,
    "iat": number,
    "jti": string,
    "type": string,
    "sub": string,
    "nbf": string,
    "exp": string,
    "userid": string,
    "obs_dt": number,
    "semidpairs": [],
    "user": {
        "role": [],
        "first": string,
        "last": string,
        "email": string
    }
}

export interface UserType {
    first?: string;
    last?: string;
    email?: string,
    roles?: string[]; //pi
    userid?: string;
    allsemids?: string[];
    semidpairs?: string[];
    semids?: string[];
    instruments?: string[];
}

export interface LoginPayload {
    token?: string | undefined;
    user?: UserType | undefined;
}

// authContext Type declaration
type authContextType = {
    loggedIn: (boolean | null);
    loading: (boolean | undefined);
    roles: (string[] | null); //pi, obs
    token: string;
    refreshtoken: string;
    user: (UserType | null | undefined);
    watchDate: string;
    login: ({ }) => void;
    storesession: ({ }) => void;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setWatchDate: React.Dispatch<React.SetStateAction<string>>;
    setRefreshToken: React.Dispatch<React.SetStateAction<string>>;
    set_localsession: ({ }) => void;
    get_localsession: () => void;
    startnewsession: ({ }) => void;
    clearsession: () => void;
    isAuthenticated: ({ }) => void;
    update_watchdate: (new_date: string) => void;
    updateRefreshToken: () => void;
}

const authContextDefaultValues: authContextType = {
    loading: false,
    loggedIn: false,
    roles: [],
    token: '',
    refreshtoken: '',
    user: null,
    watchDate: '',
    login: ({ }) => { },
    setLoading: () => { },
    setRefreshToken: () => { },
    set_localsession: ({ }) => { },
    setWatchDate: (curr_watch_date) => { },
    get_localsession: () => { },
    storesession: ({ }) => { },
    startnewsession: ({ }) => { },
    clearsession: () => { },
    isAuthenticated: ({ }) => { },
    update_watchdate: ({ }) => { },
    updateRefreshToken: () => { },
};

const AuthContext = createContext<authContextType>(authContextDefaultValues);

// function setCookie(cname:string, cvalue:string, minutes:number=24) {
//     var d = new Date();
//     d.setTime(d.getTime() + (minutes*60*1000));
//     var expires = "expires="+ d.toUTCString();
//     document.cookie = cname + "=" + cvalue + "; " + expires;
// }

export function useAuth() {
    return useContext(AuthContext);
}

type Props = {
    children: ReactNode;
};

export function AuthProvider({ children }: Props) {
    const [user, setUser] = useState<UserType | null>();
    const [loggedIn, setLoggedIn] = useState<boolean>(false);
    const [roles, setRoles] = useState<string[]>(["pub"]);
    const [token, setToken] = useState<string>("");
    const [refreshtoken, setRefreshToken] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [watchDate, setWatchDate] = useState<string>("");
    const [cookies, setCookie, removeCookie] = useCookies(['obs_watch_date'])

    useEffect(() => {
        try {
            const obs_session = get_localsession()
            // Provides Object containing - token:string and user:object
            if (obs_session) {
                if (obs_session.token) {
                    setLoggedIn(true)
                    setToken(obs_session.token)
                    setUser(obs_session.user)
                    // update_watchdate(curr_watch_date)
                    const c_refresh_token = localStorage.getItem('refresh_token');
                    if (c_refresh_token) {
                        setRefreshToken(c_refresh_token)
                    }
                }
            } else {
                setLoggedIn(false)
                setToken("")
                setUser(null)
            }

        } catch (err) {
            console.error(err)
        }

        return () => {
            setToken("")
            setUser(null)
            setLoggedIn(false)
            setWatchDate("")
        }
    }, [token])


    useEffect(() => {
        if (refreshtoken) {
            localStorage.setItem('refresh_token', refreshtoken)
        }
        return () => {}
    }, [refreshtoken])

    const storetoken = (token_: string) => {
        setToken(token);
        return token
    }

    const set_loading = (state_: boolean) => {
        return setLoading(state_);
    }

    const set_localsession = (data: any) => {
        localStorage.setItem('obs_session', JSON.stringify(data));
        localStorage.setItem('watch_date', JSON.stringify(watchDate));
    }

    const get_localsession = () => {
        const session_str = localStorage.getItem('obs_session');
        if (session_str)
            return JSON.parse(session_str);
    }

    const startnewsession = (start_time: any) => {
        // Refresh Token here!
        const freshtoken = updateRefreshToken()
        const new_date = start_time.format('YYYY-MM-DD')
        setWatchDate(new_date)
        // setCookie('obs_watch_date', new_date, { path: '/', expires: dayjs(new_date).toDate(), httpOnly: true })
    }
    const storesession = (data: any) => {
        // Called after successful login to hydrate Context
        try {
            setUser(data?.user)
            setLoggedIn(true)
            setToken(data?.token)
            setRoles(['pi'])

        } catch (err) { console.log(err) }

    }

    const clearsession = () => {
        // Called after successful login to hydrate Context
        setUser({})
        setLoggedIn(false)
        setToken("")
        setRoles(['pub'])
        setWatchDate("")
        removeCookie('obs_watch_date')

        // Clear LocalStorage or persistent sessions storage.

    }

    const update_watchdate = (new_date: string) => {
        // if (document.cookie('obs_watch_date'))
        // cookies.get('obs_watch_date');

        // // setCookie('obs_watch_date', new_date, (24*60))
        // const [cookies, setCookie, removeCookie] = useCookies(['obs_watch_date'])
        // setCookie('obs_watch_date', new_date, { path: '/', expires: dayjs(new_date).toDate(), httpOnly: true })
        try {
            // Make call to refreshToken(), this updates the semids and access
            const decoded: TokenPayload = jwt_decode(token);
            const obs_dt = dayjs.unix(decoded?.obs_dt)
            const obs_dt_str = `${obs_dt.format('YYYY-MM-DD')}`

            // Update page title to reflect date
            document.title = `${document.title} | ${new_date}`
            setWatchDate(new_date);
        } catch (err) { }

    }

    const login = (data: any) => {
        setUser(data?.user)
        setLoggedIn(true)
        setToken(data?.token)

        setRoles(data?.roles)
    }

    const logout = () => {
        console.log("Perform Logout routine. Blacklist token, clear localstorage, clearContext")
    }

    const updateRefreshToken = () => {

        const config = {
            headers: { Authorization: `Bearer ${refreshtoken}` }
        };

        const params = {
            "token": token,
            "refreshtoken": refreshtoken,
        }
        const REFRESH_URL = '/refresh_token'
        axios.post(REFRESH_URL, params, config)
            .then(data => {
                setToken(data.data.token)
                setUser(data.data.user_payload)
                setRefreshToken(data.data.refresh_token)
            }).catch(err => {
                console.log(err);
            })

        return true
    }

    const isAuthenticated = () => {
        if (user) {
            return true
        }
    }

    const value = {
        user,
        setUser,
        loggedIn,
        setLoggedIn,
        roles,
        setRoles,
        token,
        refreshtoken,
        setToken,
        loading,
        setLoading,
        login,
        storesession,
        clearsession,
        set_localsession,
        get_localsession,
        isAuthenticated,
        watchDate,
        setWatchDate,
        updateRefreshToken,
        update_watchdate,
        setRefreshToken,
        startnewsession
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthContext;
